import {html, HTMLTemplateResult} from "lit";
import {AbstractO2dElement} from "@o2d/shared";
import {customElement} from "lit/decorators/custom-element.js";
import {state} from "lit/decorators/state.js";
import {repeat} from "lit/directives/repeat.js";
import {translate} from "lit-translate";
import {container} from "tsyringe";
import {DateTime, Interval} from "luxon";

import "../../components/my-organizer/my-organizer-assigned-column.component";
import "../../components/my-organizer/my-organizer-calendar-day.component";
import {IssuePersonalDataRepository} from "../../domain/repositories/issue-personal-data.repository";
import {DateHelper} from "../../helpers/date.helper";
import type {CalendarDayRow, MyOrganizerCalendarDayComponent} from "../../components/my-organizer/my-organizer-calendar-day.component";
import type {IssuePersonalData} from "../../domain/entities/issue-personal-data.entity";
import {CommandBus} from "../../services/command-bus.service";
import {UpdateIssuePersonalDataCommand} from "../../commands/update-issue-personal-data.command";
import {
    MY_ORGANIZER_ISSUE_UNDATED_EVENT,
    type MyOrganizerIssueUndatedEventDetail,
} from "../../components/my-organizer/my-organizer-drag-context";
import {RxjsLifecycleController} from "../../components/_reactive-controllers/rxjs-lifecycle.controller";
import {EntityLiveRefreshController} from "../../components/_reactive-controllers/entity-live-refresh.controller";
import {Issue} from "../../domain/entities/issue.entity";
import {IssueRepository} from "../../domain/repositories/issue.repository";
import {bucketCalendarEntriesByDay} from "../../domain/helpers/calendar-entries-by-day.helper";
import {CALENDAR_PROJECT_UID} from "../../constants";

/** ISO weekday order (Luxon: 1 = Monday ... 7 = Sunday) — always this order
 *  regardless of the browser/OS locale's own week-start convention, which is
 *  what "Diese Woche: Montag...Sonntag" (Anke, 2026-09-07) actually asks for. */
const WEEKDAY_LABELS = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];

interface CalendarDay {
    date: DateTime;
    label: string;
}

interface CalendarWeek {
    /** i18n key of the full-width header row above the week. */
    headerKey: string;
    days: CalendarDay[];
}

/** One header per week, top to bottom — three weeks since Anke, 2026-10-16 ("3 Wochen statt jetzt
 *  nur 2"); the number of weeks follows from this list. */
const WEEK_HEADER_KEYS = [
    'personal-organizer.calendar.this-week',
    'personal-organizer.calendar.next-week',
    'personal-organizer.calendar.week-after-next',
];

/**
 * "Mein Kalender" — the page "My Organizer" mode opens on (since
 * 2026-10-17; before that the second page), alongside "Meine Boxen"
 * (`<o2d-personal-organizer-view>`), added to the sidebar submenu together
 * (Anke, 2026-09-06: "es wird unter 'my organizer' 2 Seiten geben: 'Meine
 * Boxen'... und 'Mein Kalender'"). Was a routed placeholder
 * until now — real content added 2026-09-07 (Anke: "lege dort ganz analog
 * zur Seite 'Meine Boxen' an: links die Inbox mit allen tickets... dann
 * rechts davon ein grid mit 7 Spalten und 4 Zeilen").
 *
 * Layout mirrors "Meine Boxen" exactly (same `.my-organizer` flex-row
 * wrapper, same `<my-organizer-assigned-column>` on the left, reused as-is
 * but with `dimension="date"` — see its own doc comment on why the SAME
 * component excludes by a DIFFERENT single field depending on which page
 * mounts it, per Anke's own 2026-09-07 clarification quoted there: both
 * pages' inboxes start from the identical pool and only diverge as the User
 * sorts each page's own dimension — a merely-LABELED Issue must still show
 * here, only a DATED one is excluded). On the right: a 7-column CSS grid,
 * one header row (spanning all 7 columns) plus Mon–Sun per week — "Diese
 * Woche", "Nächste Woche" and, since 2026-10-16, "Übernächste Woche" (three
 * weeks, 21 days, see `WEEK_HEADER_KEYS`) — one `<my-organizer-calendar-day>`
 * per weekday, each a drop target that stamps a dropped Issue's
 * `personalDate` with THAT cell's own date (Anke: "da ja das aktuelle datum
 * bekannt ist, lässt sich das datum der 14 tage leicht bestimmen... wenn ich
 * ein ticket in eine solche tagesbox verschiebe, dann wird dieses datum als
 * das individuelle datum abgespeichert").
 *
 * Loads every one of the current User's own `IssuePersonalData` rows ONCE
 * (`IssuePersonalDataRepository.findAllMine()`) and buckets the ones that
 * carry a `personalDate` falling on one of these 21 days by their ISO date
 * string, handing each bucket to its matching `<my-organizer-calendar-day>`
 * via `.rows=` — see that component's own doc comment for why it self-loads
 * exactly ONCE from a property instead of 21 separate per-cell queries (no
 * server-side date-range query exists, or is needed, for this small a
 * dataset).
 *
 * In the same load it fetches the calendar row's entries (the calendar
 * project's issues — public holidays and everyone's absences, exactly what the
 * timeline's calendar row shows) for these days and hands each cell its own
 * via `.calendarEntries=`, shown small and read-only above its tickets (Anke,
 * 2026-10-16).
 *
 * Live-refresh (Anke, 2026-09-09): this view owns the ONE
 * `EntityLiveRefreshController` for the whole page — see `_liveRefresh`'s
 * own doc comment for why that lives here rather than 21x on the day cells.
 *
 * Extends `AbstractO2dElement` (`@o2d/shared`), not the legacy
 * `AbstractLitElement` — mandatory for new components per ADR-007.
 */
@customElement('o2d-personal-organizer-calendar-view')
export class PersonalOrganizerCalendarView extends AbstractO2dElement {

    @state()
    private _weeks: CalendarWeek[] = [];

    /** The calendar row's entries per ISO date — see the class doc comment. */
    @state()
    private _calendarEntriesByDate: Map<string, Issue[]> = new Map();

    @state()
    private _rowsByDate: Map<string, CalendarDayRow[]> = new Map();

    @state()
    private _loaded = false;

    /** Bumped once per `_load()` — once something is loaded, only the newest
     *  load may write the view's state. `.calendarEntries` reaches the cells
     *  on EVERY load (unlike `.rows`, seeded once), so an older load finishing
     *  last would otherwise show stale calendar entries (review 0057-01). The
     *  live refresh uses the same counter through `_load()`'s return value —
     *  one counter instead of a second one per reload (review 0057-02). */
    private _loadGeneration = 0;

    /** RxJS lifecycle (auto-unsubscribe on disconnect) — required host shape
     *  for `EntityLiveRefreshController` below (must be named `rxjs`,
     *  public). */
    public readonly rxjs = new RxjsLifecycleController(this);

    /**
     * Live-refresh (Anke, 2026-09-09, follow-up to the Boxen/Inbox
     * live-refresh: "ich möchte, dass das Prinzip weiter genutzt wird") —
     * same `EntityLiveRefreshController` those two already use. Re-runs
     * `_load()` (recomputing `_rowsByDate` from a fresh `findAllMine()`),
     * then explicitly pushes each day's updated bucket into its already-
     * mounted `<my-organizer-calendar-day>` cell via `liveResync()` —
     * NOT via the `.rows` property, which those cells only ever seed
     * from ONCE (see that component's own class doc comment on why).
     * `<my-organizer-assigned-column dimension="date">` (this page's
     * left-hand Inbox) needs no extra wiring here — it already carries
     * its OWN live-refresh as of the same change.
     *
     * Generation-guarded (review-fund, 2026-09-09, round 3 — the SAME race
     * this pattern was already fixed for in `<my-organizer-assigned-
     * column>`/`<my-organizer-label-column>`, just missing here until
     * now): `EntityLiveRefreshController` calls `onReload()` per matching
     * event with no serialization of its own, so two `entity.changed`
     * events close together could otherwise start two overlapping
     * `_load()` calls — an older one resolving AFTER a newer one already
     * pushed fresher buckets into every cell must not push its own,
     * now-stale buckets over them. The guard is `_loadGeneration` inside
     * `_load()`; `onReload` pushes only when `_load()` returns `true`, i.e.
     * its load actually wrote the state.
     */
    private readonly _liveRefresh = new EntityLiveRefreshController(this, {
        getEntityClass: () => Issue,
        onReload: async () => {
            if (await this._load(this._days)) {
                this._pushLiveRowsToCells();
            }
        },
    });

    public override connectedCallback(): void {
        super.connectedCallback();

        this._weeks = PersonalOrganizerCalendarView._computeWeeks();

        this._load(this._days);
    }

    /** Every shown day, all weeks in order. */
    private get _days(): CalendarDay[] {
        return this._weeks.flatMap(week => week.days);
    }

    /**
     * Looks up each already-rendered `<my-organizer-calendar-day>` by its
     * `data-date` attribute (a plain DOM attribute set in `_renderWeek()`
     * below, independent of that component's own `.date` property/decorator
     * — just a stable handle for this lookup) and hands it its freshly
     * reloaded bucket directly — RAW, exactly like `_rowsByDate` always was
     * (this view has never filtered by Issue status). The cell itself
     * decides what to do with a DONE Issue (exclude it, or animate it away
     * if it just went done) — see `liveResync()`'s own doc comment there.
     */
    private _pushLiveRowsToCells(): void {
        for (const [isoDate, rows] of this._rowsByDate) {
            const cell = this.querySelector<MyOrganizerCalendarDayComponent>(`my-organizer-calendar-day[data-date="${isoDate}"]`);
            cell?.liveResync(rows);
        }
        // A day cell that lost its LAST row also needs to hear about it —
        // `_rowsByDate` only carries dates with at least one row.
        for (const day of this._days) {
            const isoDate = day.date.toISODate()!;
            if (this._rowsByDate.has(isoDate)) {
                continue;
            }
            const cell = this.querySelector<MyOrganizerCalendarDayComponent>(`my-organizer-calendar-day[data-date="${isoDate}"]`);
            cell?.liveResync([]);
        }
    }

    /**
     * This week's Monday (ISO weekday 1, regardless of locale — `.set()`
     * moves WITHIN the current week, so this resolves correctly no matter
     * which day `DateHelper.today` currently is) onward, one 7-day block per
     * entry in `WEEK_HEADER_KEYS`.
     */
    private static _computeWeeks(): CalendarWeek[] {
        const thisMonday = DateHelper.today.set({weekday: 1});

        const buildWeek = (weekStart: DateTime): CalendarDay[] =>
            WEEKDAY_LABELS.map((label, index) => ({date: weekStart.plus({days: index}), label}));

        return WEEK_HEADER_KEYS.map((headerKey, index) => ({headerKey, days: buildWeek(thisMonday.plus({weeks: index}))}));
    }

    /**
     * One load for all 21 cells — see the class doc comment. A row with no
     * `personalDate`, or one falling outside these 21 days (further out than
     * the last shown week), simply has no bucket and is never handed to any
     * cell; it stays reachable via `<my-organizer-assigned-column>` only if it
     * ALSO has no `personalLabel` — this view does not attempt to show "the
     * rest of the calendar", only these 21 days, per the request.
     *
     * The calendar row's entries load in parallel (`_loadCalendarEntries()`)
     * and never fail this load: without them the cells simply show none.
     *
     * A row whose `personalDate` has fallen into the PAST (before today —
     * this naturally covers "die Woche ist vorbei" once the week rolls over,
     * but also a day earlier this week that has simply already passed) is
     * reclaimed first, via `_reclaimPastDates()` below (Anke, 2026-09-07:
     * "wenn die nächste Woche beginnt und es gab noch tickets in der Woche
     * zugeordnet, die dann in der Vergangenheit liegt, dann bitte das
     * zugeordnete persönliche Datum entfernen und die Tickets wieder in der
     * Inbox anzeigen") — otherwise such a ticket would vanish permanently:
     * outside these 21 days so no cell shows it, yet still excluded from the
     * inbox because its stale `personalDate` is still set.
     *
     * @returns true when this load wrote the view's state (see `_loadGeneration`).
     */
    private async _load(days: CalendarDay[]): Promise<boolean> {
        const generation = ++this._loadGeneration;
        const isoDates = new Set(days.map(day => day.date.toISODate()));
        const calendarEntries = this._loadCalendarEntries(days);

        const myRows = await container.resolve(IssuePersonalDataRepository).findAllMine();
        await this._reclaimPastDates(myRows);

        const dated = myRows.filter(row => row.personalDate && isoDates.has(row.personalDate.toISODate()));

        const pairs = await Promise.all(dated.map(async row => ({issue: await row.issue, row})));

        const byDate = new Map<string, CalendarDayRow[]>();
        for (const pair of pairs) {
            if (!pair.issue) {
                continue;
            }
            const isoDate = pair.row.personalDate!.toISODate()!;
            const bucket = byDate.get(isoDate) ?? [];
            bucket.push(pair as CalendarDayRow);
            byDate.set(isoDate, bucket);
        }

        const calendarEntriesByDate = await calendarEntries;
        // Superseded by a newer load: that one writes the state. Unless nothing is loaded yet —
        // then this result still counts, so a newer load that fails cannot leave the page
        // without any day cells (review 0057-02).
        if (generation !== this._loadGeneration && this._loaded) {
            return false;
        }

        this._rowsByDate = byDate;
        this._calendarEntriesByDate = calendarEntriesByDate;
        this._loaded = true;
        return true;
    }

    /**
     * The calendar row's entries for `days`, bucketed per ISO date — the
     * calendar project's issues (public holidays, everyone's absences), the
     * same set the timeline's calendar row shows. Read-only here. A failed
     * request is logged and yields no entries rather than breaking the page.
     */
    private async _loadCalendarEntries(days: CalendarDay[]): Promise<Map<string, Issue[]>> {
        if (days.length === 0) {
            return new Map();
        }
        // End exclusive, one day after the last shown day.
        const interval = Interval.fromDateTimes(days[0].date, days[days.length - 1].date.plus({days: 1}));
        try {
            const entries = await container.resolve(IssueRepository).findAllByInterval(interval, [], [
                {key: 'project.uid', comparator: 'eq', value: CALENDAR_PROJECT_UID},
            ]);
            return bucketCalendarEntriesByDay(entries, days.map(day => day.date.toISODate()!));
        } catch (error) {
            console.error('[o2d-personal-organizer-calendar-view] failed to load the calendar row entries', error);
            return new Map();
        }
    }

    /**
     * Clears `personalDate` (persisted, not just locally) on every row whose
     * date lies strictly before today, mutating `rows` in place so the
     * caller's subsequent bucketing simply no longer sees them — see the
     * `_load()` doc comment above for why this exists. Fires
     * `MY_ORGANIZER_ISSUE_UNDATED_EVENT` per reclaimed Issue so an already-
     * rendered `<my-organizer-assigned-column dimension="date">` (which may
     * have loaded its own inbox before this finished) re-loads and shows it
     * again — see that component's own doc comment on this listener.
     *
     * Dispatches the N Update commands via `Promise.all`, not a sequential
     * `for` loop (review-fund, 2026-09-07) — consistent with `_load()`'s own
     * `dated.map(async row => ...)` pattern just below in this same file,
     * and keeps the initial render's latency constant instead of linear in
     * the number of stale rows.
     */
    private async _reclaimPastDates(rows: IssuePersonalData[]): Promise<void> {
        const today = DateHelper.today.toISODate();
        const stale = rows.filter(row => row.personalDate && row.personalDate.toISODate()! < today!);
        if (stale.length === 0) {
            return;
        }

        await Promise.all(stale.map(row => this._reclaimOne(row)));
    }

    private async _reclaimOne(row: IssuePersonalData): Promise<void> {
        const commandBus = container.resolve(CommandBus);

        row.personalDate = null;
        const dispatched = await commandBus.dispatchCommand(
            new UpdateIssuePersonalDataCommand('issue-personal-data', row.uid, row)
        );
        if (!dispatched) {
            console.error('[o2d-personal-organizer-calendar-view] failed to clear a stale personal date');
            return;
        }
        const issue = await row.issue;
        if (issue) {
            window.dispatchEvent(new CustomEvent<MyOrganizerIssueUndatedEventDetail>(
                MY_ORGANIZER_ISSUE_UNDATED_EVENT,
                {detail: {issueUid: issue.uid}}
            ));
        }
    }

    public render(): HTMLTemplateResult {
        return html`
            <div class="my-organizer">
                <my-organizer-assigned-column dimension="date"></my-organizer-assigned-column>
                <div class="my-organizer-calendar">
                    ${repeat(this._weeks, week => week.headerKey, week => html`
                        <div class="my-organizer-calendar__week-header">${translate(week.headerKey)}</div>
                        ${this._renderWeek(week.days)}
                    `)}
                </div>
            </div>
        `;
    }

    private _renderWeek(days: CalendarDay[]): HTMLTemplateResult {
        if (!this._loaded) {
            return html``;
        }

        return html`
            ${repeat(days, day => day.date.toISODate(), day => html`
                <my-organizer-calendar-day
                    data-date=${day.date.toISODate()}
                    .date=${day.date}
                    .label=${day.label}
                    .rows=${this._rowsByDate.get(day.date.toISODate()!) ?? []}
                    .calendarEntries=${this._calendarEntriesByDate.get(day.date.toISODate()!) ?? []}
                ></my-organizer-calendar-day>
            `)}
        `;
    }

}
